import { useQueryClient } from '@tanstack/react-query';
import { FC, useCallback, useEffect, useMemo } from 'react';
import { batch } from 'react-redux';
import { debounce } from 'throttle-debounce';

import { useMapContext } from 'shared/context/MapContext';
import { boundActionsFactory } from 'shared/store/clientSide';
import { useListingLayoutIsShortSelector } from 'shared/store/clientSide/slices/listingLayout';
import { FetchGetClustersLoadService } from 'shared/store/serverSide/commercial-listing-map/desktop/v2/map/get-clusters/load';

const DEBOUNCE_TIMEOUT = 100;

const useBoundActions = boundActionsFactory(reduxActions => ({
  setIsCurrentAreaSearchActive: reduxActions.mapControls.setIsCurrentAreaSearchActive,
  setMapConfiguration: reduxActions.mapConfiguration.setMapConfiguration,
}));

/**
 * Переработать этот менеджер, явно можно упростить, пока как будто бы лишняя логика
 */
export const MapBoundsUpdateManager: FC = () => {
  const { map } = useMapContext();
  const actions = useBoundActions();
  const queryClient = useQueryClient();
  const fetchGetClustersLoadService = useMemo(() => new FetchGetClustersLoadService(queryClient), [queryClient]);

  const isShortListing = useListingLayoutIsShortSelector();

  const updateMapConfiguration = useCallback(() => {
    batch(() => {
      actions.setMapConfiguration({
        bounds: map.getBounds(),
        center: map.getCenter(),
        zoom: map.getZoom(),
      });

      actions.setIsCurrentAreaSearchActive(true);
    });
  }, [actions, map]);

  const handleBoundsChange = useCallback(() => {
    if (!isShortListing) {
      return;
    }

    fetchGetClustersLoadService.cancelQuery().then(() => {
      updateMapConfiguration();
    });
  }, [isShortListing, fetchGetClustersLoadService, updateMapConfiguration]);

  const handleBoundsChangeDebounced = useMemo(
    () => debounce(DEBOUNCE_TIMEOUT, handleBoundsChange),
    [handleBoundsChange],
  );

  useEffect(() => {
    map.events.add('boundschange', handleBoundsChangeDebounced);

    return (): void => {
      map.events.remove('boundschange', handleBoundsChangeDebounced);
    };
  }, [map.events, handleBoundsChangeDebounced]);

  useEffect(() => {
    if (isShortListing) {
      updateMapConfiguration();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return null;
};
